/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

/**
 * The provider report as data: what the provider view of a Medaica Report
 * shows — questionnaire, reviewer observations, then the prose document —
 * flattened for a renderer that has no access to the report rules (the
 * server-side PDF). The derivations below are the ones the provider view
 * components themselves render from, so the two cannot drift:
 *
 * - title, empty-findings line, disclaimer: the constants the prose document
 *   prints (HeartReportProviderDocument); the disclaimer ends with
 *   `REPORT_LIMITATION_TEXT`
 * - demographics: the values the host passes to the provider view, which
 *   prints a placeholder for a missing one — missing ones are null here
 * - questionnaire: `providerQuestionnaireOf` — the summary flags from
 *   `getQuestionnaireFlags`, red first, and the answered questions by
 *   section; null when the panel renders nothing (HeartReportQuestionnaire)
 * - recording notes: the Report notes exactly as the patient document
 *   carries them, with no qualification line (HeartReportRecordingNotes)
 * - finding set and order: `reportFeatures(…, { viewMode: "provider" })`
 *   (HeartReportProviderDocument)
 * - each finding: `providerFindingOf` — graded by the RAW `getColorClass`
 *   (a reviewer-dragged marker does not re-grade the prose document);
 *   title `reportCardTitle`; result `reportResultText` in provider wording;
 *   reference `displayReferenceRange`; flag `providerFindingFlagText`; the
 *   review-required line; interpretation `generateProviderInterpretation`;
 *   the clinician note only when the reviewer edited it
 *   (HeartReportProviderDocument)
 * - summary, recommendations, reviewing clinician notes:
 *   `providerReportTexts` — `providerSummaryText` and
 *   `providerRecommendationsText` over the provider wording (falling back to
 *   the patient wording), and the trimmed provider notes
 *   (HeartReportProviderDocument)
 * - audience text: not part of the on-screen view — the caveat the PDF
 *   prints because the patient receives this clinician-format document
 */

import {
  REPORT_LIMITATION_TEXT,
  ReportFeature,
  currentHeartRateBpm,
  displayReferenceRange,
  generateProviderInterpretation,
  getColorClass,
  getQuestionnaireFlags,
  providerFindingFlagText,
  providerRecommendationsText,
  providerSummaryText,
  reportCardTitle,
  reportFeatures,
  reportResultText,
} from "./core"
import { reviewerObservationNotes } from "./patient-report-document"
import {
  PcgProviderReportDocument,
  PcgProviderReportFinding,
  PcgProviderReportQuestionnaire,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

const PROVIDER_REPORT_TITLE = "PCG Cardiac Screening Report"

/** Printed in the PDF only: the patient receives this clinician-format document too. */
const PROVIDER_REPORT_AUDIENCE_TEXT =
  "This report is written for clinicians. If you are the patient, please discuss it with your health care provider."

const PROVIDER_REPORT_FINDINGS_EMPTY_TEXT = "No findings available."

const PROVIDER_REPORT_DISCLAIMER_TEXT = "PCG screening only. Not an ECG or echocardiogram. " + REPORT_LIMITATION_TEXT

/**
 * One finding of the prose document. The grade is the raw `getColorClass`,
 * not the marker-honoring effective grade the cards use, so a
 * reviewer-dragged marker does not re-grade the document.
 */
const providerFindingOf = (
  feature: ReportFeature,
  features: ReportFeature[],
  hr: number | null
): PcgProviderReportFinding => {
  const colorClass = getColorClass(
    feature.name,
    feature.observed,
    feature.normalRange,
    feature.status,
    feature.scaleType,
    hr
  )
  const clinicianNote = feature.noteToDoctor && feature.noteToDoctorEdited ? String(feature.noteToDoctor).trim() : ""
  return {
    title: reportCardTitle(feature),
    resultText: reportResultText(feature, colorClass) || null,
    referenceText: displayReferenceRange(feature, hr) || null,
    flagText: providerFindingFlagText(feature, colorClass, hr) || null,
    // The roll-up's account of the accepted Timing recordings disagreeing
    // on the heart rate, stated as what happened; only the Heart Rate
    // finding carries the account
    reviewText: feature.reviewRequired && feature.reviewReason ? `Note — ${String(feature.reviewReason)}` : null,
    interpretation: generateProviderInterpretation(feature, colorClass, features, hr),
    clinicianNote: clinicianNote || null,
  }
}

/**
 * The closing texts of the prose document: the synthesized summary and
 * recommendations (provider wording first, else the patient wording) and the
 * reviewing clinician's notes; an empty one is null.
 */
const providerReportTexts = (
  report: {
    summary?: { patientText?: string; providerText?: string }
    followUpNote?: { patientText?: string; providerText?: string }
    providerNotes?: string
  },
  features: ReportFeature[],
  hr: number | null
): Pick<PcgProviderReportDocument, "summaryText" | "recommendationsText" | "clinicianNotesText"> => ({
  summaryText: providerSummaryText(report.summary?.providerText || report.summary?.patientText || "", features, hr),
  recommendationsText:
    providerRecommendationsText(report.followUpNote?.providerText || report.followUpNote?.patientText || "") || null,
  clinicianNotesText: (report.providerNotes || "").trim() || null,
})

/**
 * The questionnaire panel's content: the summary flags with the red ones
 * first, and the answered questions by section (unanswered items and the
 * sections left empty are dropped). Null when the panel renders nothing —
 * no questionnaire, or no answered question.
 */
const providerQuestionnaireOf = (questionnaire: unknown): PcgProviderReportQuestionnaire | null => {
  const sectionsIn = (questionnaire as { sections?: unknown } | null | undefined)?.sections
  if (!Array.isArray(sectionsIn)) {
    return null
  }
  const sections = sectionsIn
    .map((section: any) => ({
      title: String(section.title ?? ""),
      items: ((section.items ?? []) as any[])
        .filter((item: any) => item.a)
        .map((item: any) => ({ question: String(item.q), answer: String(item.a) })),
    }))
    .filter((section) => section.items.length > 0)
  if (sections.length === 0) {
    return null
  }
  const flags = getQuestionnaireFlags(questionnaire)
    .map((flag) => ({ text: flag.text, answer: flag.answer, level: flag.level as string }))
    .sort((a, b) => (a.level === "red" ? 0 : 1) - (b.level === "red" ? 0 : 1))
  return { flags, sections }
}

const buildProviderReportDocument = (
  report: unknown,
  {
    patientName,
    examDate,
    dob,
    age,
    sex,
    patientId,
  }: { patientName: string; examDate: string; dob?: string; age?: string; sex?: string; patientId?: string }
): PcgProviderReportDocument => {
  const document = report as {
    features?: ReportFeature[]
    questionnaire?: unknown
    recordingNotes?: unknown
    summary?: { patientText?: string; providerText?: string }
    followUpNote?: { patientText?: string; providerText?: string }
    providerNotes?: string
  }
  const features = document.features ?? []
  const hr = currentHeartRateBpm(features)
  const findings = reportFeatures(features, { viewMode: "provider" }).map((feature) =>
    providerFindingOf(feature, features, hr)
  )
  const recordingNotes = reviewerObservationNotes(document.recordingNotes)
  return {
    title: PROVIDER_REPORT_TITLE,
    audienceText: PROVIDER_REPORT_AUDIENCE_TEXT,
    patientName,
    examDate,
    dob: dob || null,
    age: age || null,
    sex: sex || null,
    patientId: patientId || null,
    questionnaire: providerQuestionnaireOf(document.questionnaire),
    recordingNotes,
    recordingNoteInstruction: null,
    findings,
    findingsEmptyText: findings.length === 0 ? PROVIDER_REPORT_FINDINGS_EMPTY_TEXT : null,
    ...providerReportTexts(document, features, hr),
    disclaimerText: PROVIDER_REPORT_DISCLAIMER_TEXT,
  }
}

export {
  PROVIDER_REPORT_AUDIENCE_TEXT,
  PROVIDER_REPORT_DISCLAIMER_TEXT,
  PROVIDER_REPORT_FINDINGS_EMPTY_TEXT,
  PROVIDER_REPORT_TITLE,
  buildProviderReportDocument,
  providerFindingOf,
  providerQuestionnaireOf,
  providerReportTexts,
}
